import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import {
  listCategories,
  createCategory,
  updateCategory,
  updateCategoryStatus,
  isCategoryError,
} from "@/infrastructure/category/categoryService";
import type { Category, CategoryErrorCode, CategoryStatus, CategoryMeta } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { Pagination } from "@/design-system";
import { CategoryFormModal } from "./components/CategoryFormModal";

const PAGE_SIZE = 10;

export const CategoryPage = memo(function CategoryPage() {
  const { t } = useI18n();
  const { canCreateCategory, canEditCategory } = useRole();
  const [items, setItems] = useState<Category[]>([]);
  const [meta, setMeta] = useState<CategoryMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Category | null>(null);
  const [filter, setFilter] = useState<"" | CategoryStatus>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCategories({ page, limit: PAGE_SIZE, search: search || undefined, status: filter || undefined });
      setItems(data.items.filter(c => !c.isSystem));
      setMeta(data.meta);
    } catch {}
    setLoading(false);
  }, [page, search, filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => { setPage(1); }, [filter, search]);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getErrorMessage = useCallback((code: CategoryErrorCode): string => {
    const map: Record<CategoryErrorCode, string> = {
      DUPLICATE_CODE: t.category.errorDuplicateCode,
      VALIDATION: t.category.errorValidation,
      NOT_FOUND: t.category.errorForbidden,
      FORBIDDEN: t.category.errorForbidden,
      UNAUTHORIZED: t.category.errorForbidden,
      UNKNOWN: t.category.errorForbidden,
    };
    return map[code];
  }, [t.category]);

  const handleCreate = useCallback(async (data: { code: string; name: string; description?: string }) => {
    try {
      await createCategory(data);
      showToast(t.category.successCreated, "success");
      setShowForm(false);
      fetchItems();
    } catch (err) {
      if (isCategoryError(err)) showToast(getErrorMessage(err.message as CategoryErrorCode), "error");
    }
  }, [fetchItems, showToast, t.category.successCreated, getErrorMessage]);

  const handleUpdate = useCallback(async (data: { id: string; code?: string; name?: string; description?: string }) => {
    try {
      await updateCategory(data);
      showToast(t.category.successUpdated, "success");
      setEditingItem(null);
      setShowForm(false);
      fetchItems();
    } catch (err) {
      if (isCategoryError(err)) {
        const code = err.message as CategoryErrorCode;
        if (code === "FORBIDDEN") showToast(t.category.errorSystem, "error");
        else showToast(getErrorMessage(code), "error");
      }
    }
  }, [fetchItems, showToast, t.category.successUpdated, t.category.errorSystem, getErrorMessage]);

  const handleToggleStatus = useCallback(async (item: Category) => {
    if (item.isSystem) { showToast(t.category.errorSystem, "error"); return; }
    const newStatus: CategoryStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateCategoryStatus({ id: item.id, status: newStatus });
      showToast(t.category.successStatusChanged, "success");
      fetchItems();
    } catch (err) {
      if (isCategoryError(err)) {
        const code = err.message as CategoryErrorCode;
        if (code === "FORBIDDEN") showToast(t.category.errorSystem, "error");
        else showToast(getErrorMessage(code), "error");
      }
    }
  }, [fetchItems, showToast, t.category.successStatusChanged, t.category.errorSystem, getErrorMessage]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.category.title}</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.category.subtitle}</p>
        </div>
        {canCreateCategory && (
          <button
            onClick={() => { setShowForm(true); setEditingItem(null); }}
            style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            {t.category.addCategory}
          </button>
        )}
      </div>

      <Filters search={search} onSearch={setSearch} filter={filter} onFilter={setFilter} />

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label={t.category.title}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.category.colCode, t.category.colName, t.category.colStatus, canEditCategory ? t.category.colActions : ""].filter(Boolean).map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 20px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <CategoryRow key={item.id} item={item} onEdit={() => { setEditingItem(item); setShowForm(true); }} onToggleStatus={() => handleToggleStatus(item)} />
                ))}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 20px 16px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <CategoryFormModal
          item={editingItem}
          onSave={(data) => editingItem ? handleUpdate({ id: editingItem.id, ...data }) : handleCreate(data as { code: string; name: string; description?: string })}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
});


const CategoryRow = memo(function CategoryRow({ item, onEdit, onToggleStatus }: { item: Category; onEdit: () => void; onToggleStatus: () => void }) {
  const { t } = useI18n();
  const { canEditCategory } = useRole();
  const isActive = item.status === "ACTIVE";
  return (
    <tr
      style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }}
      onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")}
      onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}
    >
      <td style={cell}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.code}</span>
          {item.isSystem && <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.semibold, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", padding: "2px 8px", borderRadius: "9999px" }}>{t.category.systemBadge}</span>}
        </div>
      </td>
      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.name}</span></td>
      <td style={cell}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)", color: isActive ? "#16a34a" : "#6b7280", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}>
          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} aria-hidden="true" />
          {isActive ? t.category.statusActive : t.category.statusInactive}
        </span>
      </td>
      {canEditCategory && (
        <td style={cell}>
          <div style={{ display: "flex", gap: "6px" }}>
            {!item.isSystem && (
              <>
                <RowBtn label={t.category.editCategory} onClick={onEdit} />
                <RowBtn label={isActive ? t.category.deactivate : t.category.activate} onClick={onToggleStatus} danger={isActive} />
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
});

const cell: React.CSSProperties = { padding: "14px 20px", verticalAlign: "middle" };

function RowBtn({ label, onClick, danger = false }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold, color: danger ? colors.error : "#666", background: "transparent", border: `1px solid ${danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"}`, borderRadius: "7px", padding: "5px 12px", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}
      onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? colors.error : "#111"; el.style.color = danger ? colors.error : "#111"; }}
      onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"; el.style.color = danger ? colors.error : "#666"; }}
    >
      {label}
    </button>
  );
}

const FILTER_KEYS = ["", "ACTIVE", "INACTIVE"] as const;

const Filters = memo(function Filters({ search, onSearch, filter, onFilter }: { search: string; onSearch: (v: string) => void; filter: "" | CategoryStatus; onFilter: (v: "" | CategoryStatus) => void }) {
  const { t } = useI18n();
  const labels: Record<string, string> = { "": t.category.filterAll, ACTIVE: t.category.filterActive, INACTIVE: t.category.filterInactive };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
      <input type="search" value={search} onChange={e => onSearch(e.target.value)} placeholder={t.category.searchPlaceholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", minWidth: "200px", boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: "4px" }}>
        {FILTER_KEYS.map(key => (
          <button key={key} onClick={() => onFilter(key)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: filter === key ? typography.weight.semibold : typography.weight.medium, padding: "8px 16px", borderRadius: "8px", border: `1px solid ${filter === key ? "#111" : "#e8e8e8"}`, background: filter === key ? "#111" : "#fff", color: filter === key ? "#fff" : "#666", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}>{labels[key]}</button>
        ))}
      </div>
    </div>
  );
});

function Toast({ message, type }: { message: string; type: "success" | "error" }) {
  return <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300, animation: "fadeInUp 0.3s ease" }}>{message}</div>;
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px", gap: "12px", textAlign: "center" }}>
      <div style={{ width: "48px", height: "48px", background: "#f5f5f5", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
      </div>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.category.emptyTitle}</p>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb", margin: 0, maxWidth: "300px" }}>{t.category.emptySubtitle}</p>
    </div>
  );
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}
